import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { useModalStore } from '@/store';
import Message from '@/components/Messages/Message';
import { mapSerializedMessageToMessage } from '@/constants/utils/message';

// PLUS_WINBACK_PRESSURE §2 (blurred variant): render the REAL boundary
// messages (the handful right above the visible window) using the actual
// <Message> component — so they honor the user's selected message layout
// (bubble / slim / wide) and width — then blur the whole stack.
//
// In ChatView this sits ABOVE the lock banner in scroll order (older →
// newer). Scrolling up from readable messages, the user hits the banner
// + CTA first; the blurred preview is optional depth above that.
//
// ─────────────────────────────────────────────────────────────────────────
//  TOGGLE — flip to false to A/B against the plain hide (banner-only). When
//  false this renders nothing and ChatView falls back to just the banner.
// ─────────────────────────────────────────────────────────────────────────
const CEILING_ENABLED = true;

// Convert a theme color (hex #rgb / #rrggbb, or rgb()) to an rgba string
// with the given alpha. Used so the top fade dissolves into the *chat
// background color* at 0 alpha — fading to plain `transparent` would tint
// the gradient toward black on web.
function toRgba(color, alpha) {
    if (typeof color !== 'string') return `rgba(0,0,0,${alpha})`;
    const hex = color.trim();
    if (hex[0] === '#') {
        let r;
        let g;
        let b;
        if (hex.length === 4) {
            r = parseInt(hex[1] + hex[1], 16);
            g = parseInt(hex[2] + hex[2], 16);
            b = parseInt(hex[3] + hex[3], 16);
        } else {
            r = parseInt(hex.slice(1, 3), 16);
            g = parseInt(hex.slice(3, 5), 16);
            b = parseInt(hex.slice(5, 7), 16);
        }
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    const m = hex.match(/rgba?\(([^)]+)\)/);
    if (m) {
        const [r, g, b] = m[1].split(',').map(s => parseInt(s, 10));
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return `rgba(0,0,0,${alpha})`;
}

const createStyles = (colors) => StyleSheet.create({
    wrap: {
        marginBottom: 6,
        position: 'relative',
    },
    // The blur is what sells "locked": strong enough to be unreadable, soft
    // enough that the real bubbles/layout still read as a conversation. Web
    // uses a CSS filter; native uses a real <BlurView> overlay (see render) —
    // a translucent scrim alone leaves the gated text legible on device.
    stack: {
        ...(Platform.OS === 'web' && {
            filter: 'blur(5px)',
            userSelect: 'none',
        }),
    },
    nativeBlur: {
        ...StyleSheet.absoluteFillObject,
    },
    // On web this deepens the frosted feel. On native it both tints the
    // BlurView output AND acts as a safety net: Android only truly blurs when
    // the experimental method is active, so the scrim must obscure text on its
    // own if the blur degrades to a tint-only fallback.
    scrim: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: colors.solid_color_primary,
        opacity: Platform.OS === 'web' ? 0.04 : 0.45,
    },
    // Fade the TOP of the stack into the chat background so the oldest
    // locked bubble appears to dissolve upward into nothing.
    topFade: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 56,
    },
});

function PlusLockedMessageCeiling({ preview, conversationId, chatTitle, isOwner }) {
    const { colors, theme } = useTheme();
    const styles = useMemo(() => createStyles(colors), [colors]);
    const blurTint = isDarkTheme(theme) ? 'dark' : 'light';
    const { openModal } = useModalStore(state => state.actions);

    // Map the serialized boundary messages into the same client shape the
    // chat list uses, so <Message> renders them identically.
    const messages = useMemo(
        () => (Array.isArray(preview) ? preview.map(mapSerializedMessageToMessage) : []),
        [preview]
    );

    const handlePress = useCallback(() => {
        openModal('CreditsPurchaseModal', { initialTab: 'subscribe', source: 'plus-lock-message-ceiling' });
    }, [openModal]);

    if (!CEILING_ENABLED) return null;
    if (messages.length === 0) return null;

    return (
        <Pressable
            onPress={handlePress}
            accessibilityRole="button"
            accessibilityLabel="Restore Plus to read your earlier messages"
            style={styles.wrap}
        >
            {/* pointerEvents none: the real <Message> components are inert —
                all taps fall through to the Pressable (→ upgrade modal) and
                none of their internal actions (edit/regenerate/lightbox)
                can fire. */}
            <View style={styles.stack} pointerEvents="none">
                {messages.map((message) => (
                    <Message
                        key={message.id || message._id}
                        message={message}
                        conversationId={conversationId}
                        isOwner={isOwner}
                        chatTitle={chatTitle}
                        isLastMessage={false}
                    />
                ))}
            </View>
            {/* Native has no CSS blur — overlay a real BlurView so the gated
                message text is genuinely unreadable (web uses the stack filter). */}
            {Platform.OS !== 'web' && (
                <BlurView
                    intensity={64}
                    tint={blurTint}
                    experimentalBlurMethod="dimezisBlurView"
                    style={styles.nativeBlur}
                    pointerEvents="none"
                />
            )}
            <View style={styles.scrim} pointerEvents="none" />
            <LinearGradient
                colors={[toRgba(colors.solid_color_primary, 1), toRgba(colors.solid_color_primary, 0)]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={styles.topFade}
                pointerEvents="none"
            />
        </Pressable>
    );
}

PlusLockedMessageCeiling.propTypes = {
    preview: PropTypes.arrayOf(PropTypes.object),
    conversationId: PropTypes.string,
    chatTitle: PropTypes.string,
    isOwner: PropTypes.bool,
};

export default React.memo(PlusLockedMessageCeiling);
